import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  TextField,
  IconButton,
  Typography,
  Button,
  Card,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress
} from "@mui/material";
import { Search, ChevronLeft, ChevronRight } from "lucide-react";
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";

// dependencies
import { loadMonitoringAndAssessments, localizeAssessmentType } from "../../utils/ObjectsUtils";
import Sidebar from "../../scenes/global/Sidebar";
import Topbar from "../../scenes/global/Topbar";
import Footer from "../../scenes/global/Footer";
import MonitoringCard from "../../components/Dashboard/MonitoringCard";
import AssessmentsTable from "../../components/Dashboard/AssessmentTable";
import SharingAssessments from "../../components/Dashboard/SharingAssessments";
import AddIcon from "@mui/icons-material/Add";
import { buttonStyle } from "../../components/styledComponents";
import jwt_decode from "jwt-decode";
import axios from "axios";
import { FRONTEND_URL, BACKEND_URL } from "../../config";
import { useMessageService } from '../../services/MessageService';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuthUser } from '../../contexts/AuthUserContext';
import { ASSESSMENT_TYPES_BY_USER, OptionTypes, UserType } from '../../utils/enums';

const MonitoringActionCard = ({ onCreate, onImport, getMessage }) => (
  <Card
    elevation={0}
    sx={{
      width: { xs: 200, sm: 220 },
      minWidth: { xs: 200, sm: 220 },
      height: 200,
      borderRadius: 2,
      border: "1.5px dashed #D8D8D8",
      boxShadow: "none",
      bgcolor: "#ffffff",
      display: "flex",
      flexDirection: "column",
      flexShrink: 0,
      overflow: "hidden",
    }}
  >
    <Box
      component="button"
      type="button"
      onClick={onCreate}
      sx={{
        flex: 1,
        border: 0,
        bgcolor: "transparent",
        cursor: "pointer",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 1,
        px: 2,
        color: "#1a1a1a",
        "&:hover": { bgcolor: "rgba(247, 148, 30, 0.1)" },
      }}
    >
      <Box
        sx={{
          width: 40,
          height: 40,
          borderRadius: "50%",
          bgcolor: "#F7941E",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <AddIcon sx={{ fontSize: 24, color: "#1a1a1a" }} />
      </Box>
      <Typography variant="body2" fontWeight={700} textAlign="center">
        {getMessage("table_monitoring_button_new_monitoring")}
      </Typography>
    </Box>
    <Divider />
    <Box
      component="button"
      type="button"
      onClick={onImport}
      sx={{
        py: 1.25,
        border: 0,
        bgcolor: "transparent",
        cursor: "pointer",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 0.75,
        color: "text.secondary",
        "&:hover": { bgcolor: "rgba(0,0,0,0.04)", color: "text.primary" },
      }}
    >
      <FileUploadOutlinedIcon sx={{ fontSize: 18 }} />
      <Typography variant="body2" fontWeight={500}>
        {getMessage("label_import")}
      </Typography>
    </Box>
  </Card>
);

const Dashboard = () => {

  const [expandedMonitoring, setExpandedMonitoring] = useState(null);
  const [selectedAssessments, setSelectedAssessments] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const scrollContainerRef = useRef(null);
  const [error, setError] = useState(null);

  // monitoring states
  const [currentMonitoringId, setCurrentMonitoringId] = useState(null);
  const [monitorings, setMonitorings] = useState([]);
  const [newMonitoringName, setNewMonitoringName] = useState('');
  const [newMonitoringDescription, setNewMonitoringDescription] = useState('');
  const [newMonitoringCourseId, setNewMonitoringCourseId] = useState('');
  const [courseOptions, setCourseOptions] = useState([]);
  const [coursesLoading, setCoursesLoading] = useState(false);
  const [coursesError, setCoursesError] = useState('');
  const [openMonitoringDialog, setOpenMonitoringDialog] = useState(false);
  const [openLoadCodeDialog, setOpenLoadCodeDialog] = useState(false);
  const [loadCode, setLoadCode] = useState('');
  const [loadCodeError, setLoadCodeError] = useState('');
  const [showSandboxLimitAlert, setShowSandboxLimitAlert] = useState(false);

  // assessments states
  const [currentAssessmentId, setCurrentAssessmentId] = useState(null);
  const [assessments, setAssessments] = useState([]);
  const [selectedAssessmentsIds, setSelectedAssessmentsIds] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const qrCodeRef = useRef(null);
  const largeQRCodeRef = useRef(null);
  const [editingCell, setEditingCell] = useState(null);
  const [editingCellValue, setEditingCellValue] = useState(null);
  const [newAssessmentDay, setNewAssessmentDay] = useState('');
  const [newAssessmentName, setNewAssessmentName] = useState('');
  const [newAssessmentType, setNewAssessmentType] = useState('');
  const [openAssessmentDialog, setOpenAssessmentDialog] = useState(false);
  const [isSyncingCourse, setIsSyncingCourse] = useState(false);

  const [openAssesmentCount, setOpenAssessmentsCount] = useState(0);

  const statusToOptions = {
    Draft: [OptionTypes.EDIT, OptionTypes.PREVIEW, OptionTypes.OPEN, OptionTypes.COPY, OptionTypes.DELETE, OptionTypes.DELETE_ALL_ANSWERS],
    Open: [OptionTypes.CLOSE, OptionTypes.PREVIEW, OptionTypes.COPY, OptionTypes.DELETE, OptionTypes.DELETE_ALL_ANSWERS],
    Close: [OptionTypes.COPY, OptionTypes.DELETE, OptionTypes.DELETE_ALL_ANSWERS],
  };

  // sharing states
  const { getMessage } = useMessageService();
  const { languageCode } = useLanguage();
  const { currentUser } = useAuthUser();
  const [isLoading, setIsLoading] = useState(true);
  const showCourseField = !!currentUser?.aiBeaconApiKeyCreatedAt;
  const [isLinked, setIsLinked] = useState(false);
  const [isCodeVisible, setIsCodeVisible] = useState(false);
  const [generatedCode, setGeneratedCode] = useState('');
  const navigate = useNavigate();

  // Function to check if the token is expired
  const isTokenExpired = (token) => {
    if (!token) {
      console.log("🔐 Token non trouvé dans le localStorage");
      return true;
    }
    try {
      const decoded = jwt_decode(token);
      const currentTime = Date.now() / 1000;
      const isExpired = decoded.exp < currentTime;

      if (isExpired) {
        console.log("🚫 Token expiré!", {
          expirationTime: new Date(decoded.exp * 1000).toLocaleString(),
          currentTime: new Date().toLocaleString()
        });
      } else {
        console.log("✅ Token valide jusqu'à:", new Date(decoded.exp * 1000).toLocaleString());
      }

      return isExpired;
    } catch (error) {
      console.log("❌ Erreur lors du décodage du token:", error.message);
      return true;
    }
  };

  useEffect(() => {
    /**
     * Fetches and initializes monitoring and assessment data for the dashboard.
     */
    const fetchMonitoringsAndAssessments = async () => {
      setIsLoading(true);
      try {
        const token = localStorage.getItem("token");
        if (isTokenExpired(token)) {
          console.log("🚫 Impossible de charger les données - Token expiré");
          navigate("/signin");
          return;
        }

        // Fetch monitorings and assessments
        await loadMonitoringAndAssessments(
          currentUser,
          setMonitorings,
          setAssessments,
          setCurrentMonitoringId
        );

        // Determine which monitoring to open
        let monitoringToOpen = localStorage.getItem("lastMonitoringId");
        if (!monitoringToOpen && assessments.length > 0) {
          // Find the monitoring with the most recent lastModified assessment
          const mostRecentAssessment = assessments.reduce(
            (latest, current) =>
              new Date(current.lastModification) > new Date(latest.lastModification)
                ? current
                : latest,
            assessments[0]
          );
          monitoringToOpen = mostRecentAssessment?.monitoringId || null;
        }

        setExpandedMonitoring(monitoringToOpen);
        setCurrentMonitoringId(monitoringToOpen);
      } catch (error) {
        console.error('❌ Erreur lors du chargement des données:', error);
        if (error.response?.status === 401) {
          console.log("🔐 Erreur 401 - Session expirée");
          navigate("/signin");
        }
      } finally {
        setIsLoading(false);
      }
    };

    fetchMonitoringsAndAssessments();
  }, [currentUser, navigate]);

  // Load courses for the "New monitoring" modal (only when external keys exist).
  useEffect(() => {
    if (!openMonitoringDialog) return;
    if (!showCourseField) return;

    // If we already loaded courses for this modal session, reuse them.
    if (courseOptions.length > 0) return;

    const token = localStorage.getItem("token");
    if (!token) {
      setCoursesError("No authentication token available.");
      return;
    }

    let cancelled = false;
    const loadCourses = async () => {
      setCoursesLoading(true);
      setCoursesError("");
      try {
        const coursesRes = await axios.get(
          `${BACKEND_URL}/aiBeacon/courses`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        const coursesRaw = coursesRes.data?.courses;
        if (!Array.isArray(coursesRaw)) {
          throw new Error("Unexpected response format while loading courses.");
        }

        if (!cancelled) {
          setCourseOptions(coursesRaw);
        }
      } catch (e) {
        console.error("Failed to load courses for New monitoring:", e);
        if (!cancelled) {
          setCoursesError("Failed to load courses. Please try again.");
          setCourseOptions([]);
        }
      } finally {
        if (!cancelled) setCoursesLoading(false);
      }
    };

    loadCourses();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openMonitoringDialog, showCourseField]);

  // Save the current monitoring ID in localStorage whenever it changes
  useEffect(() => {
    if (currentMonitoringId) {
      localStorage.setItem("lastMonitoringId", currentMonitoringId);
    }
  }, [currentMonitoringId]);

  useEffect(() => {
    // Debugging logs
    // console.log("currentMonitoringId", currentMonitoringId);
    // console.log("assessments", assessments);
    // console.log("monitorings", monitorings);
  }, [
    currentMonitoringId,
    assessments,
    currentAssessmentId,
    monitorings,
    isOpen,
    selectedAssessmentsIds
  ]);

  const handleImportMonitoring = () => {
    setOpenLoadCodeDialog(true);
  };

  const handleCloseLoadCodeDialog = () => {
    setOpenLoadCodeDialog(false);
    setLoadCode('');
    setLoadCodeError('');
  };

  const handleLoadCode = async () => {
    const token = localStorage.getItem("token");

    if (!token) {
      alert("No authentication token available.");
      return;
    }

    try {
      const response = await axios.put(
        `${BACKEND_URL}/users/monitorings/code/${loadCode}/startFollowing`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (response.status === 200) {
        console.log("Code loaded successfully, and monitorings/assessments updated");
        setLoadCodeError('');
        handleCloseLoadCodeDialog();

        await loadMonitoringAndAssessments(
          currentUser,
          setMonitorings,
          setAssessments,
          setCurrentMonitoringId
        );
      }
    } catch (err) {
      console.error("Error while appending code:", err);
      if (err.response?.status === 409) {
        setLoadCodeError(getMessage("label_code_already_redeemed"));
      } else if (err.response?.status === 404) {
        setLoadCodeError(getMessage("label_code_does_not_exist"));
      } else {
        setLoadCodeError(getMessage("label_error_loading_code"));
      }
    }
  };

  /**
   * Handle creation of a new monitoring
   */
  const handleCreateMonitoring = async () => {
  let errorMessage = '';
  setError(null);

  if (!newMonitoringName || !newMonitoringDescription) {
    errorMessage = getMessage('new_monitoring_error_creation');
  } else if (
    monitorings.some(monitoring => monitoring.name === newMonitoringName)
  ) {
    errorMessage = `${getMessage('new_monitoring_error_duplicate')}.`;
  }
  if (errorMessage) {
    setError(errorMessage);
    return;
  }

  // Rest of the function remains the same...
  console.log("Add new monitoring");

  try {
    const token = localStorage.getItem("token");

    const newMonitoring = {
      orderId: monitorings.length + 1,
      userId: currentUser._id,
      name: newMonitoringName,
      description: newMonitoringDescription,
      courseMoodleId: showCourseField && newMonitoringCourseId ? newMonitoringCourseId : null,
      courseName:
        showCourseField && newMonitoringCourseId
          ? courseOptions.find(c => c.id === newMonitoringCourseId)?.name || null
          : null,
      courseAiBeaconId: null,
      courseSyncedAt: null,
      creationDate: new Date(),
      lastModification: new Date(),
      options: [
        OptionTypes.DELETE,
        OptionTypes.DELETE_ALL_ANSWERS,
        OptionTypes.COPY
      ]
    };

    const response = await axios.post(
      `${BACKEND_URL}/monitorings`,
      newMonitoring,
      {
        headers: {
          Authorization: `Bearer ${token}`
        }
      }
    );

    const serverMonitoringId = response.data._id;
    newMonitoring._id = serverMonitoringId;

    setCurrentMonitoringId(serverMonitoringId);
    setMonitorings(prev => [...prev, newMonitoring]);

    if (newMonitoring.courseMoodleId) {
      await handleSyncCourseFromMoodle({
        monitoringId: serverMonitoringId,
        moodleCourseId: newMonitoring.courseMoodleId,
        currentCourseAiBeaconId: newMonitoring.courseAiBeaconId,
      });
    }

    setNewMonitoringName('');
    setNewMonitoringDescription('');
    handleCloseMonitoringDialog();
  } catch (error) {
    console.error("Error adding monitoring:", error);
  }
};

  const handleSyncCourseFromMoodle = async ({
    monitoringId = currentMonitoringId,
    moodleCourseId: explicitMoodleCourseId,
    currentCourseAiBeaconId: explicitCourseAiBeaconId,
  } = {}) => {
    const monitoring = monitorings.find(
      (entry) => entry._id === monitoringId
    );
    const moodleCourseId = explicitMoodleCourseId ?? monitoring?.courseMoodleId;
    if (!moodleCourseId) return;

    const token = localStorage.getItem("token");
    if (!token) return;

    setIsSyncingCourse(true);
    try {
      const response = await axios.post(
        `${BACKEND_URL}/aiBeacon/courses/${encodeURIComponent(moodleCourseId)}/sync`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response?.status < 300 && response?.data?.success === true) {
        const syncedCourses = Array.isArray(response?.data?.syncedCourses)
          ? response.data.syncedCourses
          : [];
        const syncedCourse = syncedCourses.find(
          (course) => String(course?.courseMoodleId) === String(moodleCourseId)
        );
        const resolvedAiBeaconId =
          syncedCourse?.courseAiBeaconId ??
          explicitCourseAiBeaconId ??
          monitoring?.courseAiBeaconId;

        setMonitorings((prev) =>
          prev.map((entry) =>
            entry._id === monitoringId
              ? {
                  ...entry,
                  courseSyncedAt: new Date().toISOString(),
                  ...(resolvedAiBeaconId
                    ? { courseAiBeaconId: String(resolvedAiBeaconId) }
                    : {}),
                }
              : entry
          )
        );
      }
    } catch (syncError) {
      console.error("Failed to sync course from Moodle:", syncError);
    } finally {
      setIsSyncingCourse(false);
    }
  };

  /**
   * Handle creation of a new assessment
   */
  const handleCreateAssessment = async () => {
    let errorMessage = '';
    setError(null);

    // Check for sandbox user restrictions
    if (currentUser?.sandbox) {
      const existingAssessmentsOfType = assessments.filter(
        assessment => assessment.monitoringId === currentMonitoringId && 
                     assessment.type === newAssessmentType
      );
      
      if (existingAssessmentsOfType.length >= 1) {
        setError(getMessage('sandbox_user_assessment_type_limit'));
        return;
      }
    }

    if (!newAssessmentName || !newAssessmentType || !newAssessmentDay) {
      errorMessage = getMessage('new_assessment_error_creation');
    } else if (
      assessments.some(assessment => 
        assessment.monitoringId === currentMonitoringId && 
        assessment.name === newAssessmentName
      )
    ) {
      errorMessage = `${getMessage('new_assessment_error_duplicate')}.`;
    }
    if (errorMessage) {
      setError(errorMessage);
      return;
    }

    try {
      const token = localStorage.getItem("token");

      const newAssessment = {
        monitoringId: currentMonitoringId,
        userId: currentUser._id,
        name: newAssessmentName,
        day: newAssessmentDay,
        type: newAssessmentType,
        status: "Draft",
        creationDate: new Date(),
        lastModification: new Date(),
        options: statusToOptions["Draft"],
      };

      const response = await axios.post(
        `${BACKEND_URL}/assessments`,
        newAssessment,
        { headers: { Authorization: `Bearer ${token}` } }
      );

    const createdAssessment = response.data;
    setCurrentAssessmentId(createdAssessment._id);
    setAssessments(prev => [...prev, createdAssessment]);

      setNewAssessmentName('');
      setNewAssessmentType('');
      handleCloseAssessmentDialog();
    } catch (error) {
      console.error("Error adding assessment:", error);
      setError("Failed to add assessment. Please try again.");
    }
  };

  const handleCloseMonitoringDialog = () => {
    setOpenMonitoringDialog(false);
    setError(null);
    setNewMonitoringCourseId("");
    setCoursesError("");
  };

  const handleCloseAssessmentDialog = () => {
    setOpenAssessmentDialog(false);
    setError(null);
  };

  const filteredMonitorings = monitorings
    .filter(monitoring =>
      monitoring.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      monitoring.description.toLowerCase().includes(searchQuery.toLowerCase())
    )
    .sort((a, b) => new Date(b.creationDate) - new Date(a.creationDate));

  const selectedMonitoring = monitorings.find(
    m => m._id === expandedMonitoring
  );

  const scroll = direction => {
    const container = scrollContainerRef.current;
    if (container) {
      const cardWidth = 300;
      const scrollAmount = direction === "left" ? -cardWidth : cardWidth;
      container.scrollBy({ left: scrollAmount, behavior: "smooth" });
    }
  };

  return (
    <Box
      display="flex"
      bgcolor="#f9f9f9"
      sx={{ height: "100%", maxWidth: "100vw", overflow: "hidden" }}
    >
      <Sidebar />
      <Box
        display="flex"
        flex={1}
        flexDirection="column"
        sx={{ minWidth: 0, width: "100%", height: "100%", overflow: "hidden" }}
      >
        <Topbar title={getMessage("label_dashboard_title")} />
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            width: "100%",
            overflow: { xs: "auto", md: "hidden" },
            display: "flex",
            flexDirection: "column",
            gap: { xs: 1.25, md: 1.5 },
            p: { xs: 2, md: 2 },
          }}
        >
          {monitorings.length > 0 ? (
            <TextField
              size="small"
              fullWidth
              placeholder={getMessage("label_search_monitoring")}
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              InputProps={{
                endAdornment: <Search size={18} />,
                sx: { bgcolor: "background.paper" }
              }}
              sx={{ width: "100%", flexShrink: 0 }}
            />
          ) : (
            <Typography variant="h6">
              {getMessage("label_no_monitorings_found")}
            </Typography>
          )}

          <Box sx={{ position: "relative", width: "100%", flexShrink: 0 }}>
            {filteredMonitorings.length > 0 && (
              <IconButton
                onClick={() => scroll("left")}
                sx={{
                  position: "absolute",
                  left: 0,
                  top: "50%",
                  transform: "translateY(-50%)",
                  zIndex: 2,
                  display: { xs: "none", sm: "inline-flex" },
                  bgcolor: "background.paper",
                  boxShadow: 2,
                  "&:hover": { bgcolor: "background.paper" }
                }}
              >
                <ChevronLeft />
              </IconButton>
            )}

            <Box
              ref={scrollContainerRef}
              sx={{
                display: "flex",
                overflowX: "auto",
                gap: 1.5,
                pb: 0.5,
                px: { xs: 1, md: 6 },
                scrollBehavior: "smooth",
                "&::-webkit-scrollbar": { display: "none" },
                msOverflowStyle: "none",
                scrollbarWidth: "none",
                width: { xs: "100%", md: "calc(100% - 48px)" },
                margin: "0 auto",
                position: "relative"
              }}
            >
              <MonitoringActionCard
                getMessage={getMessage}
                onImport={handleImportMonitoring}
                onCreate={() => {
                  setOpenMonitoringDialog(true);
                  if (currentUser?.sandbox && monitorings.length >= 1) {
                    setShowSandboxLimitAlert(true);
                  }
                }}
              />
              {filteredMonitorings.map(monitoring => (
                <MonitoringCard
                  key={monitoring._id}
                  monitoring={monitoring}
                  expandedMonitoring={expandedMonitoring}
                  setExpandedMonitoring={id => {
                    setExpandedMonitoring(id);
                    setCurrentMonitoringId(id);
                    setSelectedAssessmentsIds([]);
                  }}
                  setMonitorings={setMonitorings}
                  setCurrentMonitoringId={setCurrentMonitoringId}
                  setAssessments={setAssessments}
                  assessments={assessments}
                  monitorings={monitorings}
                />
              ))}
            </Box>

            {filteredMonitorings.length > 0 && (
              <IconButton
                onClick={() => scroll("right")}
                sx={{
                  position: "absolute",
                  right: 0,
                  top: "50%",
                  transform: "translateY(-50%)",
                  zIndex: 2,
                  display: { xs: "none", sm: "inline-flex" },
                  bgcolor: "background.paper",
                  boxShadow: 2,
                  "&:hover": { bgcolor: "background.paper" }
                }}
              >
                <ChevronRight />
              </IconButton>
            )}
          </Box>

          {/* Create Monitoring Dialog */}
          {/* Create Monitoring Dialog */}
          <Dialog
            fullWidth
            maxWidth="sm"
            open={openMonitoringDialog}
            onClose={() => {
              handleCloseMonitoringDialog();
              setShowSandboxLimitAlert(false);
            }}
          >
            <DialogTitle variant="h3">
              {getMessage("new_monitoring_create_new_monitoring")}
            </DialogTitle>
            <DialogContent>
              {showSandboxLimitAlert && currentUser?.sandbox && monitorings.length >= 1 && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  {getMessage("sandbox_user_monitoring_limit")}
                </Alert>
              )}
              <TextField
                autoFocus
                size="small"
                margin="dense"
                id="name"
                label={getMessage("label_name")}
                type="text"
                fullWidth
                value={newMonitoringName}
                onChange={e => setNewMonitoringName(e.target.value)}
                disabled={showSandboxLimitAlert}
              />
              <TextField
                margin="dense"
                id="description"
                label={getMessage("new_monitoring_description")}
                type="text"
                fullWidth
                value={newMonitoringDescription}
                onChange={e => setNewMonitoringDescription(e.target.value)}
                disabled={currentUser?.sandbox && monitorings.length >= 1}
              />
              {showCourseField && (
                <Box mt="20px">
                  <Select
                    value={newMonitoringCourseId}
                    margin="dense"
                    size="small"
                    fullWidth
                    onChange={e => setNewMonitoringCourseId(e.target.value)}
                    disabled={coursesLoading}
                    displayEmpty
                  >
                    <MenuItem value="">
                      {getMessage("label_select_course")}
                    </MenuItem>
                    {courseOptions.map(course => (
                      <MenuItem key={course.id} value={course.id}>
                        {course.name}
                      </MenuItem>
                    ))}
                  </Select>

                  {coursesLoading && (
                    <Box display="flex" justifyContent="flex-start" mt={1}>
                      <CircularProgress size={20} />
                    </Box>
                  )}
                  {coursesError && (
                    <Box mt="10px">
                      <Alert severity="error">{coursesError}</Alert>
                    </Box>
                  )}
                </Box>
              )}
              {error && error !== getMessage("sandbox_user_monitoring_limit") && (
                <Box mt="15px">
                  <Alert severity="error">
                    {error}
                  </Alert>
                </Box>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseMonitoringDialog}>
                {getMessage("label_cancel")}
              </Button>
              <Button
                onClick={handleCreateMonitoring}
                variant="contained"
                color="primary"
                sx={buttonStyle}
                disabled={showSandboxLimitAlert}
              >
                {getMessage("new_monitoring_create")}
              </Button>
            </DialogActions>
          </Dialog>

          {/* Import Monitoring Dialog */}
          <Dialog fullWidth maxWidth="sm" open={openLoadCodeDialog} onClose={handleCloseLoadCodeDialog}>
            <DialogTitle>{getMessage("load_code_dialog_title")}</DialogTitle>
            <DialogContent>
              {currentUser?.sandbox && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  {getMessage("sandbox_user_cannot_import")}
                </Alert>
              )}
              <TextField
                autoFocus
                margin="dense"
                id="loadCode"
                label={getMessage("label_code_dialog_enter")}
                type="text"
                fullWidth
                value={loadCode}
                onChange={e => setLoadCode(e.target.value)}
                error={!!loadCodeError}
                helperText={loadCodeError}
                disabled={currentUser?.sandbox} 
              />
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseLoadCodeDialog}>
                {getMessage("label_cancel")}
              </Button>
              <Button
                onClick={handleLoadCode}
                variant="contained"
                color="primary"
                sx={buttonStyle}
                disabled={currentUser?.sandbox} 
              >
                {getMessage("label_import")}
              </Button>
            </DialogActions>
          </Dialog>

          {/* Create Assessment Dialog */}
          <Dialog
            fullWidth
            maxWidth="sm"
            open={openAssessmentDialog}
            onClose={handleCloseAssessmentDialog}
          >
            <DialogTitle variant="h3">
              {getMessage("label_create_new_assessment")}
            </DialogTitle>
            <DialogContent>
              <Box display="flex" alignItems="center" flexWrap="wrap" gap={1}>
                <Typography>{getMessage("new_assessment_day")} &nbsp; </Typography>
                <TextField
                  id="day"
                  type="number"
                  autoFocus
                  size="small"
                  style={{ width: "70px" }}
                  margin="dense"
                  inputProps={{
                    min: "1",
                    style: { textAlign: "center" }
                  }}
                  sx={{
                    "& .MuiInputBase-input": {
                      textAlign: "center"
                    }
                  }}
                  value={newAssessmentDay === "" ? "" : newAssessmentDay}
                  onChange={e => {
                    const value =
                      e.target.value === ""
                        ? ""
                        : Math.max(1, parseInt(e.target.value, 10) || 1);
                    setNewAssessmentDay(value);
                  }}
                />
              </Box>

             
              <Box mt="20px" mb="5px">
                <InputLabel id="type-label">
                  {getMessage("new_assessment_type_assessment")}
                </InputLabel>
              </Box>
              <Select
                value={newAssessmentType}
                margin="dense"
                size="small"
                id="type"
                labelId="type-label"
                fullWidth
                onChange={e => setNewAssessmentType(e.target.value)}
              >
                {(ASSESSMENT_TYPES_BY_USER[currentUser.userStatus] || ASSESSMENT_TYPES_BY_USER[UserType.TEACHER]).map((type) => (
                  <MenuItem key={type} value={type}>
                    {localizeAssessmentType(type, getMessage)}
                  </MenuItem>
                ))}
              </Select>

               <Box mb="20px" mt="20px">
                <TextField
                  value={newAssessmentName}
                  autoFocus
                  size="small"
                  margin="dense"
                  id="name"
                  label={getMessage("label_name")}
                  type="text"
                  fullWidth
                  onChange={e => setNewAssessmentName(e.target.value)}
                />
              </Box>

              {error && (
                <Box mt="15px">
                  <Alert severity={
                    error === getMessage("sandbox_user_monitoring_limit") || 
                    error === getMessage("sandbox_user_assessment_type_limit") 
                      ? "info" 
                      : "error"
                  }>
                    {error}
                  </Alert>
                </Box>
              )}
            </DialogContent>

            <DialogActions>
              <Button onClick={handleCloseAssessmentDialog}>
                {getMessage("label_cancel")}
              </Button>
              <Box sx={{ display: "flex", justifyContent: "flex-end", p: 1 }}>
                <Button
                  onClick={handleCreateAssessment}
                  variant="contained"
                  color="primary"
                  sx={buttonStyle}
                >
                  {getMessage("new_assessment_create")}
                </Button>
              </Box>
            </DialogActions>
          </Dialog>

          {/* Assessments and Sharing Section */}
          {selectedMonitoring && (
            <Box sx={{ display: "flex", flexDirection: { xs: "column", lg: "row" }, gap: 2, alignItems: "stretch", flex: { xs: "none", md: 1 }, minHeight: { md: 0 } }}>
              <Box sx={{ flex: 2, minWidth: 0, minHeight: 0, display: "flex" }}>
                <AssessmentsTable
                  assessments={assessments}
                  setAssessments={setAssessments}
                  setMonitorings={setMonitorings}
                  monitorings={monitorings}
                  currentMonitoringId={currentMonitoringId}
                  onSyncCourseFromMoodle={handleSyncCourseFromMoodle}
                  isSyncingCourse={isSyncingCourse}
                  showSyncCourseButton={
                    showCourseField &&
                    !!selectedMonitoring?.courseMoodleId
                  }
                  currentAssessmentId={currentAssessmentId}
                  setCurrentAssessmentId={setCurrentAssessmentId}
                  setIsOpen={setIsOpen}
                  setOpenAssessmentsCount={setOpenAssessmentsCount}
                  selectedAssessmentsIds={selectedAssessmentsIds}
                  setSelectedAssessmentsIds={setSelectedAssessmentsIds}
                  onCreateAssessment={() => {
                    setError(null);
                    setOpenAssessmentDialog(true);
                  }}
                  fillHeight
                />
              </Box>

              {selectedAssessmentsIds.length > 0 && (
                <Box sx={{ flex: { xs: '1 1 auto', lg: '0 0 360px' }, width: { xs: '100%', lg: 360 }, maxWidth: { lg: 380 }, minWidth: { lg: 320 }, minHeight: 0, display: "flex" }}>
                  <SharingAssessments
                    selectedAssessmentsIds={selectedAssessmentsIds}
                    assessments={assessments}
                    currentMonitoringId={currentMonitoringId}
                    isMonitoringOwner={selectedMonitoring?.userId === currentUser?._id}
                    onScheduleSaved={(updatedAssessments) => {
                      setAssessments((prev) => prev.map((assessment) => {
                        const updated = updatedAssessments.find((item) => item._id === assessment._id);
                        if (!updated) {
                          return assessment;
                        }
                        return {
                          ...assessment,
                          scheduledSendAt: updated.scheduledSendAt
                            ? new Date(updated.scheduledSendAt)
                            : null,
                          scheduledSendStatus: updated.scheduledSendStatus || null,
                        };
                      }));
                    }}
                  />
                </Box>
              )}
            </Box>
          )}
        </Box>
        <Footer />
      </Box>
    </Box>
  );
};

export default Dashboard;
